import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import Button from "../Components/Button";
import ReportsLayout from "./ReportsLayout";
import useAllEventsFilterDate from "../hooks/useAllEventsFilterDate";
import {FilterContext} from "../FilterContextProvider";
import ReactToPrint from "react-to-print";
import {Link} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faSpinner} from "@fortawesome/free-solid-svg-icons";
import {exportToCSV} from "../Utility/Csv";
import {UserContext} from "../Auth/UserContextProvider";
import EventFilter from "../Sales/EventFilter";
import useApi from "../Api/Api";

const useAllProductsReport = (eventIds) => {
  const api = useApi();
  const [report, setReport] = useState({});
  const [isFetching, setIsFetching] = useState(true);
  const {
    filterData: {artist_id, start_date, end_date},
  } = useContext(FilterContext);

  const fetchReport = useCallback(() => {
    setIsFetching(true);
    const queryString = [
      `artist_id=${artist_id}&start_date=${start_date}&end_date=${end_date}`,
      ...Object.keys(eventIds).map((id) => `event_ids[]=${id}`),
    ].join("&");
    artist_id &&
    api
      .get(`/reports/all_products?${queryString}`)
      .then((r) => {
        setReport(r.data);
      })
      .catch((error) => {
        console.log("useAllProductsReport error", error);
      }).finally(() => {
        setIsFetching(false);
      }
    )
  }, [api, artist_id, start_date, end_date, eventIds]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    isFetching,
  };
};

const AllProducts = () => {
  const {filterData, setFilterData} = useContext(FilterContext);
  const {
    allEventsFilterDate: allEvents,
    eventIds: allEventsEventIds,
    setEventIds: setAllEventsEventIds,
  } = useAllEventsFilterDate("show");
  const [startingEvents, setStartingEvents] = useState({});
  const printRef = useRef();
  const {isAdmin} = useContext(UserContext);
  const [eventFilterDate, setEventFilterDate] = useState({start_date: "", end_date: "", date_button: ""});
  const {report, isFetching: isFetchingReport} = useAllProductsReport(allEventsEventIds);

  const setEventIds = (eventIds) => {
    setAllEventsEventIds(eventIds);
  };


  !isFetchingReport && report.products.forEach((prod) => {
    if (!prod.size) {
      prod.export_size = "";
    } else {
      prod.export_size = `- ${prod.product_size}`;
    }
  });

  const productSales = report?.products?.filter((prod) => prod.sold !== 0)
    .map((product) => ({
      Name: `${product.name} ${product?.export_size}`,
      Category: product.category_name,
      Units_Sold: product.sold,
      Average_Price: product.gross_sales / product.sold,
      Gross_Sales: product.gross_sales,
      Cost: product.gross_cost,
      Gross_Profit: product.gross_sales - product.gross_cost,
      Percentage_of_Gross: product.gross_sales / report?.gross_sales,
      Average_Units_per_Show: product.sold / allEvents.length,
      Per_Cap: (product.gross_sales / report?.gross_sales) * report?.per_cap,
    }));

  const totals = {
    Name: `Grand Totals`,
    Category: "",
    Units_Sold: report.units_sold,
    Average_Price: "",
    Gross_Sales: report.gross_sales,
    Cost: report.cost_of_goods_sold,
    Gross_Profit: report.gross_profit,
    Percentage_of_Gross: "",
    Average_Units_per_Show: "",
    Per_Cap: report.per_cap,
  };

  productSales?.push(totals);

  return (


    <ReportsLayout>
      <div className="mt-8 mx-8 py-10 px-12 bg-white rounded-lg border">
        {!isFetchingReport && report?.products.length > 0 && (
          <div className="flex justify-end text-sm">
            <ReactToPrint
              trigger={() => <Button className="button" text="Print"></Button>}
              content={() => printRef.current}
            />
            <Button
              className="button button-white ml-2"
              text="Export"
              onClick={() => exportToCSV(productSales, `${filterData.artist_name}_all_products_report`)}
            ></Button>
          </div>
        )}
        <div className="max-w-max">
          <Link to="/reports">
            <div className="-mt-6 mb-6 font-medium text-black text-sm">
              <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to reports
            </div>
          </Link>
        </div>
        <div className="flex flex-col text-sm w-1/2">
          <span className="text-xs pl-12">Date range/show</span>
          <EventFilter
            events={allEvents}
            eventsToRender={startingEvents}
            setEventsToRender={setStartingEvents}
            filterData={filterData}
            eventIds={allEventsEventIds}
            eventFilterDate={eventFilterDate}
            setEventFilterDate={setEventFilterDate}
            setEventIds={setEventIds}
            setFilterData={setFilterData}
          ></EventFilter>
        </div>
        <div ref={printRef} className="mt-8 mx-8 text-black">
          <div className="flex justify-between">
            <div className="mb-2 border-2 border-black p-2 min-w-max">
              <div className="text-xl font-extrabold">TM Sales Report</div>
              <div className="text-lg font-extrabold">All Products</div>
              <div className="flex">
                <div className="text-base font-normal italic">
                  <div>Artist:</div>
                  <div>Start Date:</div>
                  <div>End Date:</div>
                </div>
                <div className="ml-4 text-base font-normal">
                  <div className="text-lg font-semibold">{filterData.artist_name}</div>
                  <div className="align-text-top">
                    {filterData.start_date?.slice(5, 7)}/{filterData.start_date?.slice(8, 10)}/
                    {filterData.start_date?.slice(0, 4)}
                  </div>
                  <div>
                    {filterData.end_date?.slice(5, 7)}/{filterData.end_date?.slice(8, 10)}/
                    {filterData.end_date?.slice(0, 4)}
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute right-36 mb-2 border-2 border-black font-semibold p-2 min-w-max">
              {isFetchingReport ? (
                <div className="w-full flex justify-center h-full items-center"><FontAwesomeIcon icon={faSpinner}
                                                                                                 spin></FontAwesomeIcon>
                </div>
              ) : (<div className="flex">
                  <div className="text-right italic">
                    <div>Units Sold:</div>
                    <div>Gross Sales:</div>
                    <div>Cost:</div>
                    <div>Gross Profit:</div>
                    <div>Per Cap:</div>
                    <div>Average Gross Per Show:</div>
                    <div>Number of Shows:</div>
                  </div>
                  <div className="ml-4 text-base text-right">
                    <div>{(report?.units_sold ?? 0).toLocaleString()}</div>
                    <div>
                      {parseFloat(report?.gross_sales || 0).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </div>
                    <div>
                      {parseFloat(report?.cost_of_goods_sold || 0).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </div>
                    <div>
                      {parseFloat(report?.gross_profit || 0).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </div>
                    <div>
                      {parseFloat(report?.per_cap || 0).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </div>
                    <div>
                      {parseFloat(report?.avg_gross_per_show || 0).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </div>
                    <div>{allEvents.length}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="w-full mt-20 text-sm">
            <div className="text-xs mb-4 font-light">
              <div className="text-right h-12 flex w-full">
                <div className="w-1/4 text-left">PRODUCT</div>
                <div className="w-1/25 text-left">SIZE</div>
                <div className="w-1/12">UNITS SOLD</div>
                <div className="w-1/10">AVERAGE PRICE</div>
                <div className="w-1/8">GROSS SALES</div>
                <div className="w-1/8">COST</div>
                <div className="w-1/8">GROSS PROFIT</div>
                <div className="w-1/12">PERCENTAGE OF GROSS</div>
                <div className="w-1/12">AVERAGE UNITS PER SHOW</div>
                <div className="w-1/12">PER CAP</div>
              </div>
            </div>
            {isFetchingReport ? (
              <div className="w-full flex justify-center h-full items-center"><FontAwesomeIcon icon={faSpinner}
                                                                                               spin></FontAwesomeIcon>
              </div>
            ) : (
              <div> {report.products
                .filter((productToRender) => productToRender.sold !== 0)
                .map((productToRender, i) => {
                  return (
                    <div key={productToRender.id} className="flex text-right py-1 border-b border-black">
                      {isAdmin ? (
                        <div className="w-1/4 text-left text-blue-500">
                          <Link to={`/reports/sales/${productToRender.id}`}>{productToRender.name}</Link>
                        </div>
                      ) : (
                        <div className="w-1/4 text-left">{productToRender.name}</div>
                      )}
                      <div id={`size_${i}`} className="w-1/25 text-left">
                        {productToRender.product_size}
                      </div>
                      <div id={`sold_${i}`} className="w-1/12">
                        {productToRender.sold}
                      </div>
                      <div id={`average_price_${i}`} className="w-1/10">
                        {(productToRender.gross_sales / productToRender.sold)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                      <div id={`gross_sales_${i}`} className="w-1/8">
                        {productToRender.gross_sales?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                      <div id={`cost_${i}`} className="w-1/8">
                        {productToRender.gross_cost?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                      <div id={`gross_profit_${i}`} className="w-1/8">
                        {(productToRender.gross_sales - productToRender.gross_cost)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                      <div id={`percentage_of_gross_${i}`} className="w-1/12">
                        {((productToRender.gross_sales / report?.gross_sales) * 100)?.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}
                        %
                      </div>
                      <div id={`average_units_per_show_${i}`} className="w-1/12">
                        {(productToRender.sold / allEvents.length)?.toFixed(1)}
                      </div>
                      <div id={`per_cap_${i}`} className="w-1/12">
                        {((productToRender.gross_sales / report?.gross_sales) * report?.per_cap)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex mt-4 pt-2 text-right border-t-2 border-black">
              <div className="w-1/4 text-2xl italic font-bold text-left">Totals:</div>
              <div className="w-1/25"></div>
              <div id="gross_sold" className="w-1/12">
                Units Sold
              </div>
              <div className="w-1/10"></div>
              <div id="gross_sales" className="w-1/8">
                Gross Sales
              </div>
              <div id="gross_cost" className="w-1/8">
                Cost
              </div>
              <div id="gross_profit" className="w-1/8">
                Gross Profit
              </div>
              <div id="gross_percentage_of_gross" className="w-1/12">
                Number of Shows
              </div>
              <div id="gross_average_units_per_show" className="w-1/12">
                Average Per Show
              </div>
              <div id="gross_per_cap" className="w-1/12">
                Per Cap
              </div>
            </div>
            <div className="flex text-right font-bold">
              <div className="w-1/4 text-2xl italic"></div>
              <div className="w-1/25"></div>
              <div id="gross_sold" className="w-1/12">
                {(report?.units_sold ?? 0).toLocaleString()}
              </div>
              <div className="w-1/10"></div>
              <div id="gross_sales" className="w-1/8">
                {parseFloat(report?.gross_sales || 0).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div id="gross_cost" className="w-1/8">
                {parseFloat(report?.cost_of_goods_sold || 0).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div id="gross_profit" className="w-1/8">
                {parseFloat(report?.gross_profit || 0).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div id="gross_percentage_of_gross" className="w-1/12">
                {allEvents.length}
              </div>
              <div id="gross_average_units_per_show" className="w-1/12">
                {parseFloat(report?.avg_gross_per_show || 0).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div id="gross_per_cap" className="w-1/12">
                {parseFloat(report?.per_cap || 0).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ReportsLayout>
  );
};

export default AllProducts;
